import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Crown, Sparkles, Infinity as InfinityIcon, Palette, BarChart3 } from "lucide-react";

// Lightweight reusable premium-upgrade modal.
// Used whenever the backend returns 403 { detail: { code: "free_chapter_limit", ... } }
// or any other place we want to nudge the user.
export default function PremiumUpgradeModal({ open, onOpenChange, title, description }) {
  const navigate = useNavigate();

  const goPremium = () => {
    onOpenChange(false);
    navigate("/premium");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden rounded-3xl border-emerald-500/30 bg-white p-0 shadow-2xl shadow-emerald-500/10 dark:bg-zinc-950"
        data-testid="premium-upgrade-modal"
      >
        {/* Soft emerald glow header — matches the green/black aspirant aesthetic */}
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 px-6 py-7 text-white">
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" aria-hidden="true"/>
          <div className="relative flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 backdrop-blur-sm">
              <Crown size={22}/>
            </div>
            <div>
              <DialogTitle className="font-heading text-xl font-bold text-white">
                {title || "You've reached the free limit 🚀"}
              </DialogTitle>
              <p className="text-xs text-emerald-50/90">Syllox Premium</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          <DialogDescription className="text-sm text-zinc-600 dark:text-zinc-400">
            {description || "Unlock unlimited chapters and premium features with Syllox Premium."}
          </DialogDescription>

          <ul className="mt-5 space-y-2.5 text-sm">
            <li className="flex items-center gap-2.5 text-zinc-700 dark:text-zinc-300">
              <InfinityIcon size={16} className="shrink-0 text-emerald-500"/>
              Unlimited subjects & chapters
            </li>
            <li className="flex items-center gap-2.5 text-zinc-700 dark:text-zinc-300">
              <Palette size={16} className="shrink-0 text-emerald-500"/>
              Premium themes (NEET Green, JEE Blue & more)
            </li>
            <li className="flex items-center gap-2.5 text-zinc-700 dark:text-zinc-300">
              <BarChart3 size={16} className="shrink-0 text-emerald-500"/>
              Advanced productivity tools
            </li>
          </ul>

          <div className="mt-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-center text-sm font-medium text-emerald-700 dark:text-emerald-300" data-testid="premium-modal-pricing">
            ₹49/month &nbsp;·&nbsp; ₹299/year <span className="text-xs font-normal text-emerald-600/80 dark:text-emerald-400/80">(save 49%)</span>
          </div>

          <div className="mt-7 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="premium-modal-maybe-later"
              className="text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              Maybe later
            </Button>
            <Button
              type="button"
              onClick={goPremium}
              data-testid="premium-modal-upgrade"
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 text-white shadow-md shadow-emerald-500/30 hover:from-emerald-600 hover:to-emerald-700"
            >
              <Sparkles size={14} className="mr-1.5"/>
              Upgrade to Premium
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
